import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      icons={{
        success: (
          <CircleCheckIcon className="size-4" />
        ),
        info: (
          <InfoIcon className="size-4" />
        ),
        warning: (
          <TriangleAlertIcon className="size-4" />
        ),
        error: (
          <OctagonXIcon className="size-4" />
        ),
        loading: (
          <Loader2Icon className="size-4 animate-spin" />
        ),
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      toastOptions={{
        /*
         * `cn-toast` was the only class here and nothing in the stylesheet ever
         * defined it, so every part of a toast fell back to sonner's own
         * colours. The description came out barely legible against our popover
         * background, and the action button carried none of the button styling
         * used everywhere else.
         *
         * Mapped to design tokens instead, so a toast reads like the rest of
         * the interface in both themes.
         *
         * The `!` suffixes are not decoration. Sonner injects its own rules at
         * `[data-sonner-toast][data-styled=true] [data-description]` — three
         * selectors, so specificity (0,3,0) — which beats any single utility
         * class (0,1,0) no matter what we set. Its `[data-button]` rule is the
         * same story, and it hardcodes a filled black background that fights
         * every other button in the product. Marking these important is the
         * only way to win without unstyling the component wholesale.
         */
        classNames: {
          toast: "cn-toast items-start gap-2",
          title: "text-sm font-medium text-popover-foreground",
          description: "text-[13px]! leading-relaxed! text-muted-foreground!",
          // Outlined, like every other button that asks for a decision.
          actionButton:
            "shrink-0 self-center rounded-lg! border! border-border! bg-background! px-2.5! py-1! text-xs! font-normal! text-foreground! transition-colors hover:bg-accent!",
          cancelButton:
            "shrink-0 self-center rounded-lg! bg-transparent! px-2.5! py-1! text-xs! font-normal! text-muted-foreground! transition-colors hover:text-foreground!",
          closeButton: "border-border bg-background text-muted-foreground hover:text-foreground",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
